import { useState } from 'react';
import { CoachCard } from '../concept/coach-card';
import { ActionButton } from '../concept/action-button';
import { Eyebrow } from '../concept/eyebrow';
import type { Note, NoteAudience, NoteInput } from '../../lib/interfaces';

export interface ScanOption {
  id: string;
  /** e.g. "18 Sep 2026" */
  label: string;
}

const dateOf = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Who can read a note, said on the note. Not on hover and not in an edit
 * form: the failure this exists to prevent is a coach reading down their
 * notes, not noticing one is shared, and writing beneath it as if it were not.
 */
const AudienceMark: React.FC<{ audience: NoteAudience; clientName: string }> = ({
  audience,
  clientName,
}) => (
  <span
    data-testid={`note-audience-${audience}`}
    className={`shrink-0 rounded px-1.5 py-1 font-mono text-[8.5px] font-semibold uppercase leading-none tracking-wider ${
      audience === 'client'
        ? 'bg-accent text-accent-foreground'
        : 'border border-border text-muted-foreground'
    }`}
  >
    {audience === 'client' ? `For ${clientName}` : 'Only you'}
  </span>
);

const Textarea: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  testId: string;
}> = ({ value, onChange, placeholder, testId }) => (
  <textarea
    autoFocus
    value={value}
    onChange={(e) => onChange(e.target.value)}
    rows={4}
    maxLength={4000}
    data-testid={testId}
    placeholder={placeholder}
    className="w-full resize-y rounded-lg border border-border bg-card p-3 text-[12.5px] leading-relaxed outline-none focus:border-primary"
  />
);

const LinkButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
  danger?: boolean;
}> = ({ onClick, children, testId, danger }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-[11px] font-medium hover:underline ${danger ? 'text-destructive' : 'text-primary'}`}
  >
    {children}
  </button>
);

/**
 * Sharing is off and this note is for the client: say that it will not reach
 * them yet, where they are writing it, rather than let a coach believe they
 * have sent something.
 */
const SharingOff: React.FC<{ clientName: string; onTurnOn?: () => Promise<unknown> }> = ({
  clientName,
  onTurnOn,
}) => {
  const [busy, setBusy] = useState(false);
  return (
    <div
      data-testid="note-sharing-off"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-shell px-3 py-2 text-[11px] leading-snug text-foreground-soft"
    >
      <span>Sharing is off, so {clientName} can't see this yet.</span>
      {onTurnOn && (
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onTurnOn();
            } finally {
              setBusy(false);
            }
          }}
          className="font-medium text-primary hover:underline disabled:opacity-50"
          data-testid="note-turn-on-sharing"
        >
          {busy ? 'Turning on…' : 'Turn on sharing'}
        </button>
      )}
    </div>
  );
};

const Composer: React.FC<{
  audience: NoteAudience;
  clientName: string;
  scans: ScanOption[];
  sharingOn: boolean;
  onTurnOnSharing?: () => Promise<unknown>;
  onSave: (input: NoteInput) => Promise<unknown>;
  onDone: () => void;
}> = ({ audience, clientName, scans, sharingOn, onTurnOnSharing, onSave, onDone }) => {
  const [body, setBody] = useState('');
  const [about, setAbout] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ body: body.trim(), audience, measurementID: about || undefined });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The note was not saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="note-composer" className="flex flex-col gap-2 rounded-lg border border-border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <Eyebrow tone={audience === 'client' ? 'primary' : 'muted'}>
          {audience === 'client' ? `Message for ${clientName}` : 'Note'}
        </Eyebrow>
        <AudienceMark audience={audience} clientName={clientName} />
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {audience === 'client'
          ? `${clientName} sees this on their dashboard.`
          : `Private to you. ${clientName} never sees it.`}
      </p>
      <Textarea
        value={body}
        onChange={setBody}
        testId="note-input"
        placeholder={
          audience === 'client'
            ? 'What went well, what comes next.'
            : 'What is holding, what to change, what to watch.'
        }
      />
      {scans.length > 0 && (
        <label className="flex items-center gap-2 text-[11px] text-foreground-soft">
          About
          <select
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            data-testid="note-about"
            className="rounded-md border border-border bg-card px-2 py-1 text-[11px] outline-none focus:border-primary"
          >
            <option value="">{clientName} in general</option>
            {scans.map((s) => (
              <option key={s.id} value={s.id}>
                The {s.label} scan
              </option>
            ))}
          </select>
        </label>
      )}
      {audience === 'client' && !sharingOn && (
        <SharingOff clientName={clientName} onTurnOn={onTurnOnSharing} />
      )}
      {error && <p className="text-[11px] text-destructive">{error}</p>}
      <div className="flex gap-2">
        <ActionButton
          variant="primary"
          onClick={save}
          disabled={saving || !body.trim()}
          testId="note-save"
        >
          {saving ? 'Saving…' : audience === 'client' ? 'Save message' : 'Save note'}
        </ActionButton>
        <ActionButton onClick={onDone} disabled={saving}>
          Cancel
        </ActionButton>
      </div>
    </div>
  );
};

type Pending = null | 'edit' | 'share' | 'unshare' | 'delete';

const NoteItem: React.FC<{
  note: Note;
  clientName: string;
  scanLabel?: string;
  sharingOn: boolean;
  onTurnOnSharing?: () => Promise<unknown>;
  onUpdate: (input: NoteInput) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}> = ({ note, clientName, scanLabel, sharingOn, onTurnOnSharing, onUpdate, onDelete }) => {
  const [pending, setPending] = useState<Pending>(null);
  const [draft, setDraft] = useState(note.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (act: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await act();
      setPending(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = (
    text: React.ReactNode,
    label: string,
    act: () => Promise<unknown>,
    danger = false,
  ) => (
    <div
      data-testid={`note-confirm-${pending}`}
      className="flex flex-col gap-2 rounded-lg bg-shell px-3 py-2 text-[11px] leading-snug text-foreground-soft"
    >
      <div>{text}</div>
      <div className="flex gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => run(act)}
          data-testid="note-confirm"
          className={`font-medium hover:underline disabled:opacity-50 ${danger ? 'text-destructive' : 'text-primary'}`}
        >
          {busy ? 'Working…' : label}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setPending(null)}
          className="font-medium text-muted-foreground hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <li data-testid="note" className="flex flex-col gap-1.5 border-b border-divider pb-3 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-muted-foreground">
        <AudienceMark audience={note.audience} clientName={clientName} />
        <span className="tabular">{dateOf(note.createdAt)}</span>
        {scanLabel && <span>· about the {scanLabel} scan</span>}
      </div>

      {pending === 'edit' ? (
        <>
          <Textarea value={draft} onChange={setDraft} testId="note-edit-input" placeholder="" />
          <div className="flex gap-2">
            <ActionButton
              variant="primary"
              disabled={busy || !draft.trim()}
              onClick={() => run(() => onUpdate({ body: draft.trim() }))}
              testId="note-edit-save"
            >
              {busy ? 'Saving…' : 'Save'}
            </ActionButton>
            <ActionButton
              disabled={busy}
              onClick={() => {
                setDraft(note.body);
                setPending(null);
              }}
            >
              Cancel
            </ActionButton>
          </div>
        </>
      ) : (
        <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed">{note.body}</p>
      )}

      {pending === 'share' &&
        confirm(
          <>
            {clientName} will see this on their dashboard.
            {!sharingOn && (
              <div className="mt-2">
                <SharingOff clientName={clientName} onTurnOn={onTurnOnSharing} />
              </div>
            )}
          </>,
          `Share with ${clientName}`,
          () => onUpdate({ audience: 'client' }),
        )}
      {/* Moving a shared note back is allowed and hides it from now on. It is
          not a recall, and the coach should not be left thinking it was. */}
      {pending === 'unshare' &&
        confirm(
          `${clientName} may already have read this. Making it private hides it from now on — it does not unsend it.`,
          'Make private',
          () => onUpdate({ audience: 'coach' }),
        )}
      {pending === 'delete' &&
        confirm(
          note.audience === 'client'
            ? `Delete this message? ${clientName} will no longer see it — though they may already have read it.`
            : 'Delete this note?',
          'Delete',
          onDelete,
          true,
        )}

      {error && <p className="text-[11px] text-destructive">{error}</p>}

      {pending === null && (
        <div className="flex gap-3">
          <LinkButton onClick={() => setPending('edit')} testId="note-edit">
            Edit
          </LinkButton>
          {note.audience === 'coach' ? (
            <LinkButton onClick={() => setPending('share')} testId="note-share">
              Share with {clientName}
            </LinkButton>
          ) : (
            <LinkButton onClick={() => setPending('unshare')} testId="note-unshare">
              Make private
            </LinkButton>
          )}
          <LinkButton onClick={() => setPending('delete')} testId="note-delete" danger>
            Delete
          </LinkButton>
        </div>
      )}
    </li>
  );
};

/**
 * A coach's notes on one client: private notes, and messages for the client.
 *
 * The audience is chosen before writing, not ticked after — two ways in,
 * "Note" and "Message for Johanna", rather than one box and a checkbox under
 * it. A checkbox at the end invites the one failure that matters here: a
 * candid note, an unnoticed tick, and the client reads it. It also sets the
 * register, which is different when you write to someone than about them.
 *
 * Replaces a card that held one note per scan, overwritten on edit. Notes are
 * their own collection now (functions-api#18), each with an audience, and a
 * note can be about the client in general or about one scan.
 */
export const NotesCard: React.FC<{
  notes: Note[];
  status: 'idle' | 'loading' | 'ready' | 'error';
  error?: string | null;
  onRetry?: () => void;
  /** First name, as the notes address them. */
  clientName: string;
  /** Newest first, for the "About" choice and for labelling anchored notes. */
  scans: ScanOption[];
  sharingOn: boolean;
  onTurnOnSharing?: () => Promise<unknown>;
  onCreate: (input: NoteInput) => Promise<unknown>;
  onUpdate: (noteID: string, input: NoteInput) => Promise<unknown>;
  onDelete: (noteID: string) => Promise<unknown>;
  /** Show at most this many, with a way to the rest. */
  limit?: number;
  onShowAll?: () => void;
  className?: string;
}> = ({
  notes,
  status,
  error,
  onRetry,
  clientName,
  scans,
  sharingOn,
  onTurnOnSharing,
  onCreate,
  onUpdate,
  onDelete,
  limit,
  onShowAll,
  className,
}) => {
  const [composing, setComposing] = useState<NoteAudience | null>(null);
  const shown = limit ? notes.slice(0, limit) : notes;
  const labelOf = (id?: string) => scans.find((s) => s.id === id)?.label;

  return (
    <CoachCard title="Coach notes" testId="coach-notes" className={className}>
      {composing ? (
        <Composer
          audience={composing}
          clientName={clientName}
          scans={scans}
          sharingOn={sharingOn}
          onTurnOnSharing={onTurnOnSharing}
          onSave={onCreate}
          onDone={() => setComposing(null)}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          <ActionButton onClick={() => setComposing('coach')} testId="note-new">
            Note
          </ActionButton>
          <ActionButton onClick={() => setComposing('client')} testId="note-new-message">
            Message for {clientName}
          </ActionButton>
        </div>
      )}

      {status === 'loading' && notes.length === 0 && (
        <p className="text-[12px] text-muted-foreground">Loading notes…</p>
      )}
      {status === 'error' && (
        <p className="text-[12px] text-destructive">
          {error || 'The notes did not load.'}{' '}
          {onRetry && (
            <button type="button" onClick={onRetry} className="font-medium text-primary hover:underline">
              Try again
            </button>
          )}
        </p>
      )}
      {status === 'ready' && notes.length === 0 && !composing && (
        <p className="text-[12.5px] text-muted-foreground">No notes on {clientName} yet.</p>
      )}

      {shown.length > 0 && (
        <ul className="flex flex-col gap-3">
          {shown.map((n) => (
            <NoteItem
              key={n.ID}
              note={n}
              clientName={clientName}
              scanLabel={labelOf(n.measurementID)}
              sharingOn={sharingOn}
              onTurnOnSharing={onTurnOnSharing}
              onUpdate={(input) => onUpdate(n.ID, input)}
              onDelete={() => onDelete(n.ID)}
            />
          ))}
        </ul>
      )}

      {limit && notes.length > limit && onShowAll && (
        // self-start: a button in a column flex stretches, and its label
        // centres with it, out of line with the links above.
        <div className="self-start">
          <LinkButton onClick={onShowAll} testId="notes-show-all">
            All {notes.length} notes
          </LinkButton>
        </div>
      )}
    </CoachCard>
  );
};
